import classNames from "classnames"
import { useEffect, useRef } from "react"
import { Col, Row } from "react-bootstrap"
import Form from "react-bootstrap/Form"
import ReactQuill from "react-quill"
import "react-quill/dist/quill.snow.css"

export type WysiwygProps = {
  layout?: "vertical" | "horizontal"
  labelColSize?: number
  inputColSize?: number
  label?: string
  helperText?: string
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
  readonly?: boolean
  disabled?: boolean
  isValid?: boolean
  successMessage?: string
  errorMessage?: string
  size?: "sm" | "md" | "lg"
  rows?: number
  resize?: "none" | "both" | "horizontal" | "vertical" | "auto"
  required?: boolean
  className?: string
  style?: React.CSSProperties
}

export function WysiwygField({
  layout = "vertical",
  label,
  helperText,
  placeholder,
  labelColSize,
  inputColSize,
  value,
  onChange,
  readonly,
  disabled,
  isValid,
  successMessage,
  errorMessage,
  size,
  rows = undefined,
  resize = "vertical",
  required = false,
  className,
  style,
}: WysiwygProps) {
  const domRef = useRef<HTMLTextAreaElement>(null)

  const modules = {
    // add intestation options
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic"],
      ["link"],
    ],
   
  }

  const formats = ["bold", "italic", "link", "header"]

  useEffect(() => {
    const element = domRef.current
    if (element && resize === "auto") {
      const handler = () => {
        element.style.height = "auto"
        element.style.height = element.scrollHeight + "px"
      }
      element?.addEventListener("input", handler)
      return () => {
        element?.removeEventListener("input", handler)
      }
    }
  }, [resize])

  const requiredMarker = required ? <span className="text-danger">*</span> : undefined
  const labelCmp = label ? (
    <Form.Label className="mb-0">
      {label}
      {requiredMarker}
    </Form.Label>
  ) : undefined
  const helperTextCmp = helperText ? <Form.Text className="text-muted mt-0">{helperText}</Form.Text> : undefined
  const controlCmp = (
    <ReactQuill
      value={value ?? ""}
      onChange={onChange}
      modules={modules}
      formats={formats}
      style={{
        backgroundColor: "white",
        height: "auto",
      }}
      className={classNames({'is-invalid': isValid === false, 'is-valid': isValid === true})}
      
    />
  )
  let semanticTextCmp = undefined
  
  if (errorMessage) {
    semanticTextCmp = <Form.Control.Feedback type="invalid">{errorMessage}</Form.Control.Feedback>
  } else if (successMessage) {
    semanticTextCmp = <Form.Control.Feedback type="valid">{successMessage}</Form.Control.Feedback>
  }

  if (layout === "horizontal") {
    return (
      <Form.Group as={Row} className={className} style={style}>
        <Col xs={labelColSize ?? 4} className="d-flex flex-column">
          {labelCmp}
          {helperTextCmp}
        </Col>
        <Col xs={inputColSize ?? 8} className="d-flex flex-column align-items-stretch">
          {controlCmp}
          {semanticTextCmp}
        </Col>
      </Form.Group>
    )
  } else {
    return (
      <Form.Group as={Row} className={className} style={style}>
        <Col xs={inputColSize ?? 12} className="d-flex flex-column mb-2">
          {labelCmp}
          {helperTextCmp}
          {controlCmp}
          {semanticTextCmp}
        </Col>
      </Form.Group>
    )
  }
}
